/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useState } from "react";
import { colors } from "../../../components/colors";
import { effects } from "../../../components/effects";
import * as authSelectors from "../../auth/authSelectors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StyledText } from "../../../assets/typography/typography";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  OverwriteMenuModalFormValues,
  OverwriteMenuModalProduct,
} from "./OverwriteMenuModal";
import { Tooltip } from "antd";
import { routes } from "../../../appRoutingHelpers";
import { formatMoneyAmount } from "../../../util";
import { useSelector } from "react-redux";
import {
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  useFormContext,
} from "react-hook-form";
import { MenuProduct, OverwriteType } from "../models/Menus";
import { MenuOverwriteProductModal } from "./MenuOverwriteProductModal";
import { defaultImage } from "./MenuProductItem";
import { MenuOverwriteActionLabel } from "./MenuOverwriteActionLabel";
import { MenuOverwriteButtonRow } from "./MenuOverwriteButtonRow";
import { transformPriceInput } from "../../../components/forms/inputs/PriceInput";
import { ExternalLinkWrapper } from "../../../components/ExternalLinkWrapper";

const priceContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const wrapperStyle = css`
  position: relative;
  display: flex;
  overflow: clip;
  flex-direction: column;
  border-radius: 8px;z
  background: ${colors.white};
  box-shadow: ${effects.buttonPrimary};
`;

const topContainerStyle = css`
  display: flex;
  width: 100%;
  height: 100%;
  text-align: start;
  flex-direction: column;
  gap: 8px;
`;

const removeButtonStyle = css`
  display: flex;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  background-color: ${colors.white};
  box-shadow: ${effects.buttonPrimary};
  border-radius: 9999px;
  &:hover {
    background-color: ${colors.whiteHover};
  }
`;

const toolTipStyle = css`
  display: flex;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
`;
const sortKeyStyle = css`
  background-color: rgba(0, 0, 0, 0.45);
  ${toolTipStyle}
`;

const panelStyle = css`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 8px;
  right: 8px;
  z-index: 110;
  gap: 8px;
`;

const imageWrapperStyle = css`
  position: relative;
`;

const grabHandleWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const sortKeyWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 120;
`;

const arrowStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.45);
  border-radius: 4px;
`;

const originalSortKeyWrapperStyle = css`
  opacity: 0.4;
`;

const tooltipStyle = {
  fontFamily: "Inter",
  fontSize: "14px",
};

const OverwriteEligibleText = ({
  overwriteValue,
  hiddenValue,
  unavailableValue,
  value,
  formattedOverwriteValue,
  isPriceDescription,
}: {
  overwriteValue: string;
  hiddenValue: boolean;
  unavailableValue: boolean;
  value: string;
  formattedOverwriteValue?: string;
  isPriceDescription?: boolean;
}) => {
  const preWrapStyle = css`
    white-space: pre-wrap;
  `;

  const lineThroughStyle = css`
    ${(overwriteValue || (overwriteValue === "" && isPriceDescription)) &&
    `
    text-decoration: line-through;
    `}
  `;

  const colorStyle = css`
    color: ${isPriceDescription ? colors.errorMedium : colors.textPrimary};
  `;

  return (
    <StyledText
      color={
        hiddenValue ||
        unavailableValue ||
        overwriteValue ||
        (overwriteValue === "" && isPriceDescription)
          ? colors.textSecondary
          : isPriceDescription
          ? colors.errorMedium
          : colors.textPrimary
      }
      css={preWrapStyle}
      className={`${
        isPriceDescription ? "bodySubtextRegular" : "bodyTextSemibold"
      } mb-0`}
    >
      {value && <span css={lineThroughStyle}>{value}</span>}
      {overwriteValue && (
        <span css={colorStyle}>
          {" "}
          {formattedOverwriteValue || overwriteValue}
          {isPriceDescription && !value ? ` (added)` : ""}
        </span>
      )}
    </StyledText>
  );
};

const priceComponentWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const PriceComponent = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <div css={priceComponentWrapperStyle}>
      <Tooltip
        title={title}
        arrow={false}
        placement="right"
        overlayInnerStyle={tooltipStyle}
      >
        <div css={toolTipStyle}>
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
            css={css`
              display: flex;
            `}
          >
            {icon}
          </StyledText>
        </div>
      </Tooltip>
      {children}
    </div>
  );
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  product: OverwriteMenuModalProduct;
  originalMenuProducts: MenuProduct[] | undefined;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<OverwriteMenuModalFormValues, "products">;
}

export const MenuOverwriteProductSelector = ({
  product,
  originalMenuProducts,
  remove,
  update,
  ...rest
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const formMethods = useFormContext<OverwriteMenuModalFormValues>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { watch } = formMethods;

  const unavailableValue = product.overwriteValues.isUnavailable;
  const onUnavailableChange = (value: boolean) => {
    if (product.fieldIndex || product.fieldIndex === 0) {
      update(product.fieldIndex, {
        ...actualProduct,
        overwriteValues: {
          ...actualProduct.overwriteValues,
          isUnavailable: value,
        },
      });
    }
  };

  const hiddenValue = product.overwriteValues.isHidden;
  const onHiddenChange = (value: boolean) => {
    if (product.fieldIndex || product.fieldIndex === 0) {
      update(product.fieldIndex, {
        ...actualProduct,
        overwriteValues: {
          ...actualProduct.overwriteValues,
          isHidden: value,
        },
      });
    }
  };

  const onTypeChange = (value: OverwriteType) => {
    if (product.fieldIndex || product.fieldIndex === 0) {
      update(product.fieldIndex, {
        ...actualProduct,
        overwriteValues: {
          ...actualProduct.overwriteValues,
          type: value,
        },
      });
    }
  };

  const actualProduct = watch(`products.${product.fieldIndex!}`);

  const imageStyle = css`
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 8px;
    ${(hiddenValue || unavailableValue) &&
    `
    filter: blur(2px);
    `}
  `;

  const ItemStyle = css`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 8px;
    width: 100%;
    div {
      div {
        .imageOverlay {
          display: none;
        }
        .panel {
          display: none;
        }
      }
    }
    border: none;
    gap: 16px;
    cursor: grab;
    &:hover {
      background: ${colors.whiteHover};
      div {
        div {
          .imageOverlay {
            display: flex;
          }
          .panel {
            display: flex;
          }
        }
      }
    }
  `;

  const imageOverlayStyle = css`
    position: absolute;
    top: 0px;
    right: 0px;
    left: 0px;
    height: 150px;
    background-color: rgba(0, 0, 0, 0.25);
    z-index: 100;
    border-radius: 8px;
  `;

  const isOnOriginalMenu = !!originalMenuProducts?.find(
    (p) => p.id === actualProduct.id
  );

  const overwriteTitle = product.overwriteValues.title;

  const overwritePriceDescription = product.overwriteValues.priceDescription;

  const overwritePrice = product.overwriteValues.priceValue;

  const overwritePriceDelivery = product.overwriteValues.priceValueDelivery;

  return (
    <div css={wrapperStyle} {...rest}>
      <div css={ItemStyle}>
        <div css={topContainerStyle}>
          <div css={grabHandleWrapperStyle}>
            <StyledText
              className="bodySubtextRegular mb-0"
              color={
                hiddenValue || unavailableValue
                  ? colors.textSecondary
                  : colors.textPrimary
              }
            >
              <FontAwesomeIcon icon={solid("grip")} />
            </StyledText>
          </div>
          <div css={imageWrapperStyle}>
            {product.imageUrl ? (
              <img src={product.imageUrl} css={imageStyle} alt="product" />
            ) : (
              <img src={defaultImage} css={imageStyle} alt="defaultproduct" />
            )}
            <div css={sortKeyWrapperStyle}>
              <Tooltip
                title="Sort position"
                arrow={false}
                placement="right"
                overlayInnerStyle={tooltipStyle}
              >
                <div css={sortKeyStyle}>
                  <StyledText
                    className="bodySubtextSemibold mb-0"
                    color={colors.white}
                  >
                    {actualProduct.sortKey}
                  </StyledText>
                </div>
              </Tooltip>
              {actualProduct.originalSortKey !== actualProduct.sortKey &&
                isOnOriginalMenu && (
                  <>
                    <div css={[originalSortKeyWrapperStyle, arrowStyle]}>
                      <FontAwesomeIcon
                        color={colors.white}
                        icon={solid("arrow-left")}
                      />
                    </div>
                    <Tooltip
                      css={originalSortKeyWrapperStyle}
                      title="Original sort position"
                      arrow={false}
                      placement="right"
                      overlayInnerStyle={tooltipStyle}
                    >
                      <div css={sortKeyStyle}>
                        <StyledText
                          className="bodySubtextSemibold mb-0"
                          color={colors.white}
                        >
                          {actualProduct.originalSortKey}
                        </StyledText>
                      </div>
                    </Tooltip>
                  </>
                )}
            </div>
            <div className="panel" css={panelStyle}>
              <Tooltip
                title="Edit product"
                arrow={false}
                placement="left"
                overlayInnerStyle={tooltipStyle}
              >
                <ExternalLinkWrapper
                  css={removeButtonStyle}
                  href={routes.productEdit(
                    organizationKeyName,
                    actualProduct.id
                  )}
                >
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    <FontAwesomeIcon
                      icon={solid("arrow-up-right-from-square")}
                    />
                  </StyledText>
                </ExternalLinkWrapper>
              </Tooltip>
            </div>
            <div className="imageOverlay" css={imageOverlayStyle} />
            {(hiddenValue ||
              unavailableValue ||
              !isOnOriginalMenu ||
              overwriteTitle ||
              overwritePriceDescription !== undefined ||
              overwritePrice ||
              overwritePrice === 0 ||
              overwritePriceDelivery === 0 ||
              overwritePriceDelivery) && (
              <MenuOverwriteActionLabel
                onButtonClick={() => {
                  update(product.fieldIndex!, {
                    ...product,
                    overwriteValues: {
                      type:
                        product.overwriteValues.type === OverwriteType.ADD
                          ? OverwriteType.ADD
                          : OverwriteType.NONE,
                      isHidden: false,
                      isUnavailable: false,
                      title: "",
                      priceValue: "",
                      priceValueDelivery: "",
                      priceDescription: undefined,
                    },
                    id: actualProduct.id,
                  });
                }}
                overwritePriceDescription={overwritePriceDescription as string}
                isHiddenValue={hiddenValue!}
                isUnavailableValue={unavailableValue!}
                overwriteTitle={overwriteTitle!}
                overwritePrice={overwritePrice as string}
                overwritePriceDelivery={overwritePriceDelivery as string}
                product={product}
                isOnOriginalMenu={isOnOriginalMenu}
              />
            )}
          </div>
          <OverwriteEligibleText
            overwriteValue={overwriteTitle!}
            hiddenValue={hiddenValue!}
            unavailableValue={unavailableValue!}
            value={product.name}
          />
          {(product.priceDescription ||
            overwritePriceDescription !== undefined) && (
            <OverwriteEligibleText
              overwriteValue={overwritePriceDescription!}
              hiddenValue={hiddenValue!}
              isPriceDescription={true}
              unavailableValue={unavailableValue!}
              value={product.priceDescription || ""}
            />
          )}
        </div>
        <div css={priceContainerStyle}>
          <PriceComponent
            title="Product price for pick up"
            icon={<FontAwesomeIcon icon={solid("person-walking")} />}
          >
            <OverwriteEligibleText
              overwriteValue={
                overwritePrice || overwritePrice === 0
                  ? overwritePrice.toString()
                  : ""
              }
              hiddenValue={hiddenValue!}
              unavailableValue={unavailableValue!}
              value={formatMoneyAmount(
                product.price.amount,
                product.price.currency
              )}
              formattedOverwriteValue={formatMoneyAmount(
                transformPriceInput(
                  overwritePrice as string,
                  product.price.currency
                ),
                product.price.currency
              )}
            />
          </PriceComponent>
          <PriceComponent
            title="Product price for delivery"
            icon={<FontAwesomeIcon icon={solid("person-biking")} />}
          >
            <OverwriteEligibleText
              overwriteValue={
                overwritePriceDelivery || overwritePriceDelivery === 0
                  ? overwritePriceDelivery.toString()
                  : ""
              }
              hiddenValue={hiddenValue!}
              unavailableValue={unavailableValue!}
              value={formatMoneyAmount(
                product.priceDelivery.amount,
                product.priceDelivery.currency
              )}
              formattedOverwriteValue={formatMoneyAmount(
                transformPriceInput(
                  overwritePriceDelivery as string,
                  product.priceDelivery.currency
                ),
                product.priceDelivery.currency
              )}
            />
          </PriceComponent>
        </div>
      </div>
      <MenuOverwriteButtonRow
        hiddenValue={hiddenValue!}
        isOnOriginalMenu={isOnOriginalMenu}
        unavailableValue={unavailableValue!}
        onHiddenChange={(value) => {
          onTypeChange(OverwriteType.CHANGE);
          onHiddenChange(value);
        }}
        onUnavailableChange={(value) => {
          onTypeChange(OverwriteType.CHANGE);
          onUnavailableChange(value);
        }}
        remove={remove}
        openEditModal={() => setIsModalOpen(true)}
        product={product}
      />
      <MenuOverwriteProductModal
        remove={remove}
        update={update}
        actualProduct={actualProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={product}
        isOnOriginalMenu={isOnOriginalMenu}
      />
    </div>
  );
};
